'use client'

import { usePathname } from 'next/navigation'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { SlugPageContentSkeleton } from '@src/app/[country]/[lang]/[[...slug]]/modules/SlugPageContentSkeleton'
import { AuthUtils } from '@src/auth/auth.util'
import { useBroadcastChannel } from '@src/hooks/useBroadcastChannel'
import { getLocaleFromPath } from '@src/i18n/locale.util'
import type { CountryCode, Lang } from '@src/i18n/locales'
import { ServerActions } from '@src/services/server-actions.service'
import type { UserIdentity } from '@src/services/user.service'
import { useURL } from '@src/utils/URLProvider'
import { BROADCAST_TOPIC, QUERY_PARAMS } from '@src/utils/constants.util'
import { log } from '@src/utils/log.util'

type User = {
  guid?: string
  email?: string
  firstName?: string
  lastName?: string
  coachId?: string
  purchaseRegion?: string
  userLocale?: string
  userCountry?: CountryCode
  userLang?: Lang
  startDate?: string
  roles?: string[]
  userMembershipState?: string
  hasBodiAccess?: boolean
  entitlements?: string[]
  address?: Auth.UserProfile['address']
}

const buildUser = ({
  identity,
  authProfile,
}: {
  identity?: UserIdentity | null
  authProfile?: Auth.UserProfile | null
}): User | null => {
  if (!identity || !authProfile) return null
  return {
    guid: identity?.guid,
    email: identity?.email ?? authProfile?.email,
    firstName: identity?.firstName ?? authProfile?.given_name,
    lastName: identity?.lastName ?? authProfile?.family_name,
    coachId: identity?.coachId,
    purchaseRegion: identity?.purchaseRegion,
    userLocale: identity?.userLocale,
    userCountry: identity?.userLocale?.split('_')[1] as CountryCode,
    userLang: identity?.userLocale?.split('_')[0] as Lang,
    startDate: identity?.startDate,
    roles: identity?.role,
    userMembershipState: identity?.userMembershipState,
    entitlements: identity?.entitlementGroups,
    hasBodiAccess: identity?.hasBodiAccess,
    address: authProfile?.address,
  }
}

export interface SessionContextType {
  authSession: Auth.Session | null
  commercetoolsSession: Commercetools.Session | null
  user: User | null
  isCheckingSSO: boolean
  isComingFromLogin: boolean
  isComingFromLogout: boolean
  loginOAuth: (args: Auth.LoginOAuthArgs) => Promise<{ exchangeUrl: string }>
  login: (args?: Auth.LoginArgs) => void
  logout: (args?: Auth.LogoutArgs) => void
  refreshToken: () => Promise<Pick<SessionContextType, 'authSession' | 'user'>>
  setAuthSession: (session: Auth.Session | null) => void
  getLoginHref: (args?: Auth.LoginArgs) => string
}

export interface SessionProviderProps {
  children: React.ReactNode
  authSession?: SessionContextType['authSession']
  commercetoolsSession?: SessionContextType['commercetoolsSession']
  isCheckingSSO?: SessionContextType['isCheckingSSO']
  isComingFromCallback?: boolean
  shouldRedirectToSSO?: boolean
  authProfile: Auth.UserProfile | null
  userIdentity: UserIdentity | null
}
const SessionContext = createContext<SessionContextType>({
  authSession: null,
  commercetoolsSession: null,
  user: null,
  isCheckingSSO: false,
  isComingFromLogin: false,
  isComingFromLogout: false,
  loginOAuth: (_args: Auth.LoginOAuthArgs) => Promise.resolve({ exchangeUrl: '' }),
  login: (_args?: Auth.LoginArgs) => {},
  logout: (_args?: Auth.LogoutArgs) => {},
  refreshToken: () => Promise.resolve({ authSession: null, user: null }),
  setAuthSession: (_session: Auth.Session | null) => {},
  getLoginHref: () => '',
})

export function SessionProvider(props: SessionProviderProps) {
  const currentURL = useURL()
  const { userIdentity, authProfile } = props
  const [authSession, setAuthSession] = useState(props.authSession ?? null)
  const [commercetoolsSession] = useState(props.commercetoolsSession ?? null)
  const [user, setUser] = useState(buildUser({ identity: userIdentity, authProfile }))
  const [isCheckingSSO, setCheckingSSO] = useState(!!props.isCheckingSSO)
  const [isComingFromCallback, setComingFromCallback] = useState(!!props.isComingFromCallback)
  const isComingFromLogin = isComingFromCallback && !!user && !isCheckingSSO
  const isComingFromLogout = isComingFromCallback && !user && !isCheckingSSO

  const { publish, subscribe } = useBroadcastChannel()
  const pathname = usePathname()

  const getLoginHref = useCallback(
    (args: Auth.LoginArgs = {}) => {
      const params = new URLSearchParams({
        [QUERY_PARAMS.redirectUrl]: currentURL.href,
        [QUERY_PARAMS.authLocale]: getLocaleFromPath(pathname).code,
        ...(args as Record<string, string>),
      })
      return `/login?${params.toString()}`
    },
    [pathname, currentURL]
  )

  useEffect(() => {
    if (props.shouldRedirectToSSO) {
      log.info('[SessionProvider] Redirecting to SSO...')
      const redirectUrl = encodeURIComponent(location.href)
      const url = new URL(`/sso?redirectUrl=${redirectUrl}`, location.href)
      location.href = url.toString()
      return
    }

    const checkCookies = async () => {
      const isCheckingSSO = await AuthUtils.getIsCheckingSSO()
      const isComingFromCallback = await AuthUtils.getIsComingFromCallback()
      setCheckingSSO(isCheckingSSO)
      setComingFromCallback(isComingFromCallback)
      if (isCheckingSSO) await AuthUtils.clearCheckingSSO()
      if (isComingFromCallback) await AuthUtils.clearComingFromCallback()
    }
    checkCookies()
  }, [pathname, props.shouldRedirectToSSO])

  // Listen for login and logout events from other windows
  useEffect(() => {
    subscribe(BROADCAST_TOPIC.LOGOUT, () => window.location.reload())
    subscribe(BROADCAST_TOPIC.LOGIN, () => window.location.reload())
    if (isComingFromLogout) publish(BROADCAST_TOPIC.LOGOUT)
    if (isComingFromLogin) publish(BROADCAST_TOPIC.LOGIN)
  }, [publish, subscribe, isComingFromLogout, isComingFromLogin])

  const loginOAuth = useCallback(async function loginOAuth(args: Auth.LoginOAuthArgs) {
    return await ServerActions.serial.loginOAuth(args)
  }, [])

  const login = useCallback(
    async function login(args: Auth.LoginArgs = {}) {
      location.href = getLoginHref(args)
    },
    [getLoginHref]
  )

  const logout = useCallback(async function logout(args: Auth.LogoutArgs = {}) {
    const params = new URLSearchParams({
      [QUERY_PARAMS.redirectUrl]: location.href,
      ...(args as Record<string, string>),
    })
    location.href = `/logout?${params.toString()}`
  }, [])

  const refreshToken = useCallback(
    async function refresh() {
      if (!authSession) return { authSession: null, user: null }
      await ServerActions.serial.refreshAuthToken(authSession)
      const [_authSession, authProfile, identity] = await Promise.all([
        ServerActions.parallel.getAuthSession(),
        ServerActions.parallel.getAuthUserProfile(),
        ServerActions.parallel.getUserIdentity(),
      ])
      const user = buildUser({ identity, authProfile })
      setUser(user)
      setAuthSession(_authSession)
      return { authSession: _authSession, user }
    },
    [authSession]
  )

  globalThis.login = login
  globalThis.logout = logout

  return (
    <SessionContext.Provider
      value={{
        authSession,
        commercetoolsSession,
        user,
        isCheckingSSO,
        isComingFromLogin,
        isComingFromLogout,
        loginOAuth,
        login,
        logout,
        refreshToken,
        setAuthSession,
        getLoginHref,
      }}
    >
      {props.shouldRedirectToSSO ? <SlugPageContentSkeleton /> : props.children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  try {
    return useContext(SessionContext)
  } catch (error: any) {
    throw new Error(`useSession must be used within a SessionProvider`)
  }
}
